import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router";
import { Card, Button } from "components/ui";
import { Page } from "components/shared/Page";
import "styles/mobile-responsive.css";
import { transformLeadDetailsApiData } from "utils/leadDataTransforms";
import { LeadOptimisticStateProvider, useLeadOptimisticState } from "contexts/LeadOptimisticStateContext";

// Constants and Services
import { getLead, bulkUpdateLeadStage, getLeadJourneyIndicators, getRelatedLeads } from "services/leadsService";
import { getApplicationForms } from "services/paymentServices";
import LeadRightDrawerContent from "components/leads/LeadDetails/LeadRightDrawerContent";
import LeadDetailsHeader from "components/leads/LeadDetails/LeadDetailsHeader";
import LeadBasicInfo from "components/leads/LeadDetails/LeadBasicInfo";
import StatusCards from "components/leads/LeadDetails/StatusCard";
import ProgressStepper from "components/leads/LeadDetails/ProgressStepper";
import SidebarNavigation from "components/leads/LeadDetails/SidebarNavigation";
import LeadRightSidebar from "components/leads/LeadDetails/LeadRightSidebar";
import LeadDetailsForm from "components/leads/LeadDetails/LeadForm";
import TimelineView from "components/leads/LeadDetails/TimeLineView";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { useConfirmModal } from "hooks/useConfirmModal";
import QuickLeadModal from "components/leads/LeadActions/QuickLeadModal";

// New Tab Components
import PaymentDetailsTab from "components/leads/LeadDetails/PaymentDetailsTab";
import AccountsApprovalTab from "components/leads/LeadDetails/AccountsApprovalTab";
import ApplicationFormViewTab from "components/leads/LeadDetails/ApplicationFormViewTab";
import NotesTab from "components/leads/LeadDetails/NotesTab";
import CalendarTab from "components/leads/LeadDetails/CalendarTab";
import CommunicationsTab from "components/leads/LeadDetails/CommunicationsTab";
import DocumentsTab from "components/leads/LeadDetails/DocumentsTab/DocumentsTab";
import TicketsTab from "components/leads/LeadDetails/TicketsTab";
import GoogleMeetTab from "components/leads/LeadDetails/GoogleMeetTab";
import OfferLetterTab from "components/leads/LeadDetails/OfferLetterTab";
import LeadManagementTab from "components/leads/LeadDetails/LeadManagementTab";
import { toast } from "sonner";
import StageChangeModal from "components/leads/LeadActions/StageChangeModal";


// Modal Components
import AssignOwnerModal from "components/leads/LeadActions/AssignOwnerModal";
import SetPriorityModal from "components/leads/LeadActions/SetPriorityModal";
import LeadAnalyticsModal from "components/leads/LeadActions/LeadAnalyticsModal";
import WhatsAppMessageModal from "components/leads/LeadActions/WhatsAppMessageModal";
import EmailMessageModal from "components/leads/LeadActions/EmailMessageModal";
import SMSMessageModal from "components/leads/LeadActions/SMSMessageModal";
import FollowUpScheduleModal from "components/leads/LeadActions/FollowUpScheduleModal";
import CallModal from "components/leads/LeadActions/CallModal";
import { createFollowUp, updateFollowUp, updateFollowUpStatus, getFollowUpEvents } from "services/followUpService";
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  UserIcon,
  ClockIcon,
  FlagIcon,
  ChatBubbleLeftEllipsisIcon,
  Cog6ToothIcon,
  // PhoneIcon,
  // EnvelopeIcon,
  // CalenderDaysIcon,
  // DevicePhoneMobileIcon,
  // ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Select, Textarea } from "components/ui/Form";
import { Badge } from "components/ui";
import SetInteractionLevelModal from "components/leads/LeadActions/SetInteractionLevelModal";
import { hasPermission } from "utils/permissionsUtils";
import Error401 from "../errors/401";

const LeadDetailsInner = ({ leadIdProp = null, leadDataProp = null, isModal = false, onClose = null, onFullClose = null, entityName = null, onLeadUpdate = null, onDataLoaded = null, initialSelectedTab = "timeline", initialSubTab = 0, scrollToSection = null, openNotesAddModal = false, setOpenNotesAddModal = null }) => {

  const contentRef = useRef(null);
  const finalEntityName = entityName || "Lead"; // Default to Lead if not provided
  const isInitialLoad = useRef(true);
  const optimisticDataRef = useRef({});
  const params = useParams();

  const originalLeadId = leadIdProp || params.id;
  // activeLeadId tracks which related-lead tab the user is viewing.
  // Defaults to originalLeadId so the clicked lead's tab is selected.
  const [activeLeadId, setActiveLeadId] = useState(originalLeadId || null);
  // Reset activeLeadId whenever the original lead changes (user opens a different lead)
  const prevOriginalLeadIdRef = useRef(null);
  if (prevOriginalLeadIdRef.current !== originalLeadId) {
    prevOriginalLeadIdRef.current = originalLeadId;
    setActiveLeadId(originalLeadId || null);
  }
  // The leadId used for fetching: use activeLeadId only if user clicked a tab
  const leadId = activeLeadId || originalLeadId;
  const [relatedLeads, setRelatedLeads] = useState([]);
  const [primaryLeadData, setPrimaryLeadData] = useState(leadDataProp || null);

  // Guard: track the last leadId we already fetched so we don't double-fetch
  const lastFetchedLeadIdRef = useRef(null);

  // Fetch related leads only when the original lead changes
  useEffect(() => {
    if (originalLeadId) {
      getRelatedLeads(originalLeadId).then(res => {
        if (res.success) {
          setRelatedLeads(res.data || []);
        }
      }).catch(console.error);
    }
  }, [originalLeadId]);
  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
  const [selectedContentTab, setSelectedContentTab] = useState("payment-details");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(!leadDataProp);
  const [error, setError] = useState(null);
  const [showRightPanel, setShowRightPanel] = useState(false);
  // const [rawLeadData, setLeadData] = useState(leadDataProp || null);

  const {
    leadData,
    setLeadData,
    localStage,
    localSubStage,
    localPriority,
    localInteractionLevel,
    setLocalStage,
    setLocalSubStage,
    setLocalPriority,
    setLocalInteractionLevel
  } = useLeadOptimisticState();

  const [apiResponse, setApiResponse] = useState(null);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFlaged, setIsFlaged] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [progressData, setProgressData] = useState({});
  const [showQuickLeadModal, setShowQuickLeadModal] = useState(false);
  const [selectedFormForApplication, setSelectedFormForApplication] = useState(null);
  const [isComingFromPaymentFlow, setIsComingFromPaymentFlow] = useState(false);

  const stageName = leadData?.stage || leadData?.StageName || "N/A";
  
  // Determine if viewing a past lead (not the most recent one) or duplicate lead
  const isPastLead = relatedLeads.length > 1 && relatedLeads[0] && (relatedLeads[0].Id || relatedLeads[0].id) !== activeLeadId;
  const isDuplicateLead = Boolean(
    leadData?.IsDuplicate ||
    leadData?.isDuplicate ||
    leadData?.ParentLeadId ||
    leadData?.parentLeadId ||
    (!activeLeadId && (leadDataProp?.IsDuplicate || leadDataProp?.isDuplicate || leadDataProp?.ParentLeadId || leadDataProp?.parentLeadId))
  );
  const isReadOnly = Boolean(leadData?.IsStudent || isPastLead || isDuplicateLead);

  const [visitedTabs, setVisitedTabs] = useState(new Set([initialSelectedTab]));

  useEffect(() => {
    if (selectedTab) {
      setVisitedTabs(prev => new Set([...prev, selectedTab]));
    }
  }, [selectedTab]);



  // Stage change confirmation modal
  const [stageConfirmModal, setStageConfirmModal] = useState({
    show: false,
    state: "pending",
    loading: false,
  });
  const [stageChangeData, setStageChangeData] = useState(null);

  // Modal states for all action modals
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showInteractionLevelModal, setShowInteractionLevelModal] =
    useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [selectedFollowUpForStatus, setSelectedFollowUpForStatus] = useState(null);
  const [statusFormData, setStatusFormData] = useState({
    status: '',
    statusId: null,
    outcome: '',
    outcomeId: null,
    eventTypeId: null,
    remarks: ''
  });
  const [followUpEvents, setFollowUpEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // Internal state for notes modal (works in both page and modal mode)
  const [internalOpenNotesAddModal, setInternalOpenNotesAddModal] = useState(false);


  // Status options (same as FollowUpsCalendar)
  const statusOptions = [
    { id: 1, name: 'Pending' },
    { id: 2, name: 'Completed' },
    { id: 3, name: 'Missed' },
    { id: 4, name: 'Cancelled' },
    { id: 5, name: 'Failed' },
    { id: 6, name: 'Overdue' }
  ];

  // Final statuses that cannot be updated further
  const FINAL_STATUS_IDS = [2, 3, 4, 5]; // Completed, Missed, Cancelled, Failed
  const FINAL_STATUS_NAMES = ['Completed', 'Missed', 'Cancelled', 'Failed'];

  // Helper to check if a follow-up status is final
  const isStatusFinal = (followUp) => {
    if (!followUp) return false;
    const statusId = followUp.StatusId || followUp.statusId;
    const statusName = followUp.StatusName || followUp.status;
    return FINAL_STATUS_IDS.includes(statusId) || FINAL_STATUS_NAMES.includes(statusName);
  };
  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load follow-up events when status modal opens

  useEffect(() => {
    if (showStatusModal) {
      loadFollowUpEvents();
    }
  }, [showStatusModal]);

  // Handle external note add modal trigger - switch to notes tab
  useEffect(() => {
    if (openNotesAddModal) {
      setSelectedTab('notes');
    }
  }, [openNotesAddModal]);

  const loadFollowUpEvents = async () => {
    setLoadingEvents(true);
    try {
      const result = await getFollowUpEvents();
      if (result.success) {
        setFollowUpEvents(result.data);
      } else {
        console.error("❌ Failed to load follow-up events:", result.error);
        setFollowUpEvents([]);
      }
    } catch (error) {
      console.error("💥 Error loading follow-up events:", error);
      setFollowUpEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  // Fetch lead data function - extracted for reuse
  const fetchLeadData = async (silent = false) => {
    // Handle optimistic updates if an object is passed
    if (typeof silent === 'object' && silent !== null) {
      setLeadData(prev => ({
        ...prev,
        ...silent
      }));
    }

    if (!leadId) {
      setError("No lead ID provided");
      setLoading(false);
      return;
    }

    try {
      if (!silent || typeof silent === 'object') setLoading(true);
      setError(null);

      if (silent === true || (silent && typeof silent === 'object')) {
        if (typeof silent === 'object') setLoading(false);
        // Give the backend a moment to update before we refetch
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const result = await getLead(leadId, true);

      if (result.success && result.data.data) {
        setApiResponse(result.data.data);

        // Transform API response to component-friendly format
        const transformedData = transformLeadDetailsApiData(
          result.data.data,
          leadId,
        );

        // Merge with optimistic data if present to prevent stale API data from reverting the UI
        setLeadData(prev => ({
          ...transformedData,
          ...optimisticDataRef.current,
          ...(typeof silent === 'object' ? silent : {})
        }));
        if (!activeLeadId || String(leadId) === String(originalLeadId)) {
          setPrimaryLeadData(transformedData);
        }
        if (onDataLoaded) onDataLoaded(transformedData);
      } else {
        // Fallback to dummy data if API fails
        console.warn("API failed, using dummy data:", result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error("Error fetching lead data:", err);
      setError(err.message);
      // Fallback to dummy data
    } finally {
      setLoading(false);
      // Notify parent about the update if this is a refresh (not initial load)
      if (!isInitialLoad.current && onLeadUpdate) {
        onLeadUpdate(leadId, 'update');
      }
      isInitialLoad.current = false;
    }
  };

  // Fetch lead data when component mounts or leadId changes
  useEffect(() => {
    // ✅ Reset optimistic state and clear stale data when switching leads
    setLocalStage(null);
    setLocalSubStage(null);
    setLocalPriority(null);
    setLocalInteractionLevel(null);

    if (!leadId) return;

    // ✅ Prevent re-fetching the same lead twice.
    //    leadDataProp is intentionally NOT in deps — onDataLoaded updates it
    //    which would cause an infinite loop. Instead we use a ref guard.
    if (lastFetchedLeadIdRef.current === leadId) {
      // Already fetched for this leadId — skip.
      // But still hydrate from leadDataProp if it matches (covers pre-loaded data).
      const matchesId = leadDataProp && (
        String(leadDataProp.id || leadDataProp.LeadId) === String(leadId) ||
        String(leadDataProp.leadId) === String(leadId)
      );
      if (matchesId && !leadData) {
        setLeadData({ ...leadDataProp });
        setLoading(false);
      }
      return;
    }

    // Mark this leadId as being fetched
    lastFetchedLeadIdRef.current = leadId;

    // ✅ If the parent already passed full data for this lead, use it directly
    const matchesId = leadDataProp && (
      String(leadDataProp.id || leadDataProp.LeadId) === String(leadId) ||
      String(leadDataProp.leadId) === String(leadId)
    );

    if (matchesId) {
      setLeadData({ ...leadDataProp });
      setLoading(false);
      return;
    }

    // Force clear old data so we don't see "ghost" data while loading
    setLeadData(null);
    fetchLeadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const lastInitialTabRef = useRef(initialSelectedTab);

  // Update selected tab when initialSelectedTab changes (for deep linking)
  useEffect(() => {
    // Only update if the initialSelectedTab prop actually CHANGED from its previous value
    if (initialSelectedTab && initialSelectedTab !== lastInitialTabRef.current) {
      setSelectedTab(initialSelectedTab);
      lastInitialTabRef.current = initialSelectedTab;
    }
  }, [initialSelectedTab]);

  // Auto-scroll to section and set correct sub-tab when scrollToSection is provided
  useEffect(() => {
    if (scrollToSection && leadData) {
      // If the scrollToSection is a sub-tab of the leads profile
      const isSubTab = [
        "lead-details",
        "offer-letter",
        "payment-details",
        "follow-up-calendar",
        "application-form",
        "google-meet",
      ].includes(scrollToSection);

      if (isSubTab) {
        setSelectedTab("leads-tab");
        setSelectedContentTab(scrollToSection);
      } else {
        // Handle main tabs
        const mainTabMapping = {
          'notes': 'notes',
          'documents': 'documents',
          'timeline': 'timeline',
          'communications': 'communications',
          'tickets': 'tickets'
        };
        if (mainTabMapping[scrollToSection]) {
          setSelectedTab(mainTabMapping[scrollToSection]);
        }
      }

      // Minimal delay (50ms) to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const element = document.getElementById(`section-${scrollToSection}`);
        if (element) {
          element.scrollIntoView({
            behavior: "auto",
            block: "start",
            inline: "nearest",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [scrollToSection, leadData]);

  const handleRefreshLead = async () => {
    await fetchLeadData(true);  // this already reloads the lead API
    if (onLeadUpdate) onLeadUpdate();
  };


  const handleSave = async () => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsEditing(false);
    setLoading(false);
  };
  // Standardized stage change confirmation
  const stageChangeConfirm = useConfirmModal({
    type: "stage-change",
    autoCloseSuccess: 1500,
    onConfirm: async (stageData) => {
      const stageName = stageData.leadStage;
      const subStageName = stageData.subStage;

      // ✅ Optimistic ref update
      optimisticDataRef.current = {
        ...optimisticDataRef.current,
        stage: stageName,
        subStage: subStageName
      };

      // ✅ Single state update (FIXED)
      setLeadData(prev => ({
        ...prev,
        stage: stageName,
        subStage: subStageName,
        StageName: stageName,
        SubStageName: subStageName
      }));

      // ✅ Local UI state
      setLocalStage(stageName);
      setLocalSubStage(subStageName);

      // ✅ API call
      const result = await bulkUpdateLeadStage({
        leadIds: [leadData.id],
        stageId: stageData.stageId,
        subStageId: stageData.subStageId,
        remarks: stageData.notes || "",
        leadScore: stageData.leadScore,
      });

      if (result.success) {
        let formIdToOpen = null;
        if (result.data) {
          try {
            const parsedData =
              typeof result.data === "string"
                ? JSON.parse(result.data)
                : result.data;

            if (parsedData.formId) {
              formIdToOpen = parsedData.formId;
            }
          } catch (error) {
            console.error("Error parsing formId from response:", error);
          }
        }

        const isClosedStage = stageName.toLowerCase().includes("close") || stageName.toLowerCase().includes("won") || stageName.toLowerCase().includes("win");

        if (isClosedStage) {
          if (!formIdToOpen) {
            try {
              const freshLeadRes = await getLead(leadData.id, true);
              if (freshLeadRes.success && freshLeadRes.data?.data) {
                const freshLead = freshLeadRes.data.data;
                formIdToOpen = freshLead?.FormId || freshLead?.formId || freshLead?.ApplicationFormId || freshLead?.applicationFormId ||
                               freshLead?.BasicInformation?.ApplicationFormId || freshLead?.BasicInformation?.applicationFormId ||
                               freshLead?.BasicInformation?.FormId || freshLead?.BasicInformation?.formId;
              }
            } catch (fetchError) {
              console.error("Failed to fetch fresh lead details in details page:", fetchError);
            }
          }

          let resolvedFormId = formIdToOpen || leadData?.FormId || leadData?.formId || leadData?.ApplicationFormId || leadData?.applicationFormId;

          if (!resolvedFormId) {
            try {
              const formsRes = await getApplicationForms();
              if (formsRes.success && Array.isArray(formsRes.data) && formsRes.data.length > 0) {
                resolvedFormId = formsRes.data[0].FormId || formsRes.data[0].formId;
              }
            } catch (formsError) {
              console.error("Failed to fetch fallback application forms in details:", formsError);
            }
          }

          if (resolvedFormId) {
            setSelectedFormForApplication({ FormId: resolvedFormId });

            setTimeout(() => {
              setShowQuickLeadModal(true);
            }, 500);
          }
        }

        // ✅ Refetch latest data
        await new Promise(resolve => setTimeout(resolve, 500));

        const refreshResult = await getLead(leadData.id);
        if (refreshResult.success && refreshResult.data.data) {
          const transformedData = transformLeadDetailsApiData(
            refreshResult.data.data,
            leadData.id
          );

          setLeadData(transformedData);
        }

        if (onLeadUpdate) {
          onLeadUpdate(leadData.id, "update");
        }
      }

      return result;
    },

    messages: {
      pending: {
        title: "Change Lead Stage?",
        description: `Are you sure you want to change the stage for ${leadData?.name || "this lead"
          }?`,
        actionText: "Change Stage",
      },
      success: {
        title: "Stage Changed Successfully",
        description: `Successfully updated the stage for ${leadData?.name || "the lead"
          }.`,
      },
    },
  });

  const handleStageChange = async (stageData, resetFormData = null) => {
    // Check if stageData is valid
    if (!stageData?.leadId || !stageData.stageId) {
      console.error("Missing required stage change data");
      toast.error("Error: Stage ID is missing. Please select a valid stage.");
      return;
    }

    try {
      setStageChangeData(stageData);

      // Call the form reset function if provided (from modal/panel)
      if (resetFormData && typeof resetFormData === "function") {
        resetFormData();
      }

      // Close any open modals/panels
      setShowStageModal(false);

      // Open confirmation modal
      stageChangeConfirm.openModal(stageData);
    } catch (error) {
      console.error("Error in handleStageChange:", error);
      toast.error("An error occurred while processing the stage change.");
    }
  };

  const handleOwnerChange = async () => {
    await fetchLeadData(true);
  };


  // Handle follow-up scheduling
  const handleFollowUpScheduled = async (followUpData) => {
    const isEditing = editingFollowUp !== null;

    try {
      let result;
      if (isEditing && editingFollowUp) {
        // Update existing follow-up
        result = await updateFollowUp(editingFollowUp.id || editingFollowUp.FollowUpId, followUpData);
      } else {
        // Create new follow-up
        result = await createFollowUp(followUpData);
      }

      setShowFollowUpModal(false);
      setEditingFollowUp(null);

      // Refresh lead data to update upcoming follow-ups
      await fetchLeadData(true);

      return true;
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'scheduling'} follow-up:`, error);
      // Error toast is shown in the service function
      return false;
    }
  };

  const handleEditFollowUp = (followUp) => {
    // Transform the follow-up data to the format expected by the modal
    const editData = {
      id: followUp.FollowUpId || followUp.id,
      leadId: leadData?.id || followUp.LeadId || followUp.leadId,
      userId: followUp.UserId || followUp.userId,
      userName: followUp.LeadName || followUp.userName || leadData?.name,
      status: followUp.StatusName || followUp.status,
      statusId: followUp.StatusId || followUp.statusId,
      outcomeId: followUp.OutcomeId || followUp.outcomeId,
      remarks: followUp.Remarks || followUp.remarks,
      notes: followUp.Notes || followUp.notes, // Missing field
      Notes: followUp.Notes || followUp.notes, // Missing field (PascalCase)
      scheduledTime: followUp.scheduledTime || followUp.ScheduledTime,
      date: followUp.ScheduledDate || followUp.date,
      priority: followUp.Priority || followUp.priority,
      // Use AssignedToId (GUID) instead of AssignedTo (name)
      assignedTo: followUp.AssignedToId || followUp.assignedToId || followUp.AssignedTo || followUp.assignedTo,
      assignedToName: followUp.AssignedTo || followUp.AssignedToName || followUp.assignedToName,
      channelId: followUp.ChannelId || followUp.channelId,
      type: followUp.type
    };

    setEditingFollowUp(editData);
    setShowFollowUpModal(true);
  };

  const handleUpdateFollowUpStatus = (followUp) => {
    // Check if status is already final
    if (isStatusFinal(followUp)) {
      toast.error('Cannot update status', {
        description: `This follow-up is already marked as ${followUp.StatusName || followUp.status}`,
        duration: 3000,
      });
      return;
    }

    // Set the selected follow-up with proper field mapping
    const followUpData = {
      ...followUp,
      // Map AssignedToId to assignedTo for consistency
      assignedTo: followUp.AssignedToId || followUp.assignedToId || followUp.AssignedTo || followUp.assignedTo,
      assignedToName: followUp.AssignedTo || followUp.AssignedToName || followUp.assignedToName
    };

    setSelectedFollowUpForStatus(followUpData);

    // Match the existing status by name to get the correct local statusOption id
    const currentStatusName = followUp.StatusName || followUp.status || '';
    const matchedStatus = statusOptions.find(
      s => s.name.toLowerCase() === currentStatusName.toLowerCase()
    );

    // Pre-fill the status form
    setStatusFormData({
      status: matchedStatus?.name || currentStatusName || '',
      statusId: matchedStatus?.id || null,
      outcome: followUp.EventName || followUp.eventName || '',
      outcomeId: followUp.OutcomeId || followUp.outcomeId || null,
      eventTypeId: followUp.EventTypeId || followUp.eventTypeId || followUp.OutcomeId || followUp.outcomeId || null,
      remarks: followUp.Remarks || followUp.remarks || '',
      Notes: followUp.Notes || followUp.notes,
      notes: followUp.Notes || followUp.notes
    });

    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {

    // Validate required fields
    if (!statusFormData.status || !statusFormData.statusId) {
      toast.error('Please select a status');
      return;
    }
    if (!selectedFollowUpForStatus) {
      toast.error('No follow-up selected');
      return;
    }

    try {
      // Prepare status data for individual update (use ID not name)
      const statusData = {
        status: statusFormData.status,
        statusId: statusFormData.statusId,
        outcomeId: statusFormData.outcomeId || 0,
        eventTypeId: statusFormData.eventTypeId,
        remarks: statusFormData.remarks || "",
        // Use AssignedToId (the GUID) instead of AssignedTo (the name)
        assignedTo: selectedFollowUpForStatus.assignedTo || selectedFollowUpForStatus.AssignedToId || selectedFollowUpForStatus.assignedToId,
        priority: selectedFollowUpForStatus.Priority || selectedFollowUpForStatus.priority || "medium",
        newScheduledDate: selectedFollowUpForStatus.ScheduledDate || selectedFollowUpForStatus.date
          ? new Date(selectedFollowUpForStatus.ScheduledDate || selectedFollowUpForStatus.date).toISOString()
          : new Date().toISOString(),
        newScheduledTime: selectedFollowUpForStatus.ScheduledTime || selectedFollowUpForStatus.scheduledTime || "09:00"
      };


      // Call the API to update the status
      await updateFollowUpStatus(selectedFollowUpForStatus.FollowUpId || selectedFollowUpForStatus.id, statusData);

      // Success toast is shown in the service function
      setShowStatusModal(false);
      setSelectedFollowUpForStatus(null);

      // Refresh lead data to update upcoming follow-ups
      await fetchLeadData();
    } catch (error) {
      console.error('Error updating follow-up status:', error);
      // Error toast is shown in the service function
    }
  };

  const handleAddFollowUp = () => {
    setEditingFollowUp(null);
    setShowFollowUpModal(true);
  };
  // Refresh only progress journey data
  const refreshProgressJourney = useCallback(async (forceRefresh = false) => {
    try {
      const response = await getLeadJourneyIndicators(leadId, forceRefresh);
      if (response.success && response.data) {
        setProgressData(response.data);
      } else {
        console.error('Failed to refresh progress journey:', response.error);
      }
    } catch (err) {
      console.error('Error refreshing progress journey:', err);
    }
  }, [leadId]);

  // Refresh progress journey when application details tab is clicked
  useEffect(() => {
    if (selectedTab === 'application-details' && leadId) {
      refreshProgressJourney(true);
    }
  }, [selectedTab, leadId, refreshProgressJourney]);
  const handlePriorityChange = async (priority) => {
    try {
      setShowPriorityModal(false);

      const priorityValue = typeof priority === 'object' ? (priority.Priority || priority.priority) : priority;
      setLocalPriority(priorityValue);

      const updateObj = typeof priority === 'object'
        ? priority
        : { priority: priority, Priority: priority };
      setLeadData(prev => ({ ...prev, ...updateObj }));
      toast.success("Priority updated successfully");
      await fetchLeadData(priority);
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error("Failed to update priority. Please try again.");
    }
  };
  const handleInteractionLevelChange = async (interactionLevel) => {
    try {
      setShowInteractionLevelModal(false);

      const levelName = typeof interactionLevel === 'object' ? (interactionLevel.InteractionLevelName || interactionLevel.LeadStatus) : interactionLevel;
      setLocalInteractionLevel(levelName);

      const updateObj = typeof interactionLevel === 'object' ? interactionLevel : { InteractionLevelName: interactionLevel, LeadStatus: interactionLevel };
      setLeadData(prev => ({ ...prev, ...updateObj }));
      toast.success("Interaction updated successfully");
      await fetchLeadData(interactionLevel);
    } catch (error) {
      console.error("Error updating interaction level:", error);
      toast.error("Failed to update interaction level. Please try again.");
    }
  };

  const handleTabChange = useCallback((tabId) => {
    setSelectedTab(tabId);
    if (tabId === 'application-details') {
      refreshProgressJourney(true);
    }
  }, [setSelectedTab, refreshProgressJourney]);

  const handleStepClick = (stepId) => {
    // Don't allow clicking on offer letter step if it's filtered out for Indian leads
    if (leadData?.country?.toLowerCase() === 'india' && stepId === 3) {
      return;
    }

    switch (stepId) {
      case 1:
        // Application Details -> No longer in right panel, maybe go to documents?
        setSelectedTab("documents");
        setSelectedContentTab("payment-details"); // Safe default
        break;
      case 2:
        // Document Upload -> Documents tab (reset content tab)
        setSelectedTab("documents");
        setSelectedContentTab("payment-details"); // Reset to default
        break;
      case 3:
        // Offer Letter Generated -> Navigate to offer letter tab
        setSelectedTab("offer-letter");
        break;
      case 4:
        // Payment Link -> Payment Details tab
        setSelectedTab("payment-details");
        break;
      case 5:
        // Payment Successful -> Payment Details tab
        setSelectedTab("payment-details");
        break;
      case 6:
        // Application Initiated -> Application Details tab
        handleTabChange("application-details");
        break;
      case 7:
        // Application Submitted -> Application Details tab
        handleTabChange("application-details");
        break;
      case 8:
        // Interview Scheduled -> Google Meet tab
        setSelectedTab("lead-management");
        break;
      case 9:
        // Fee Payment Link -> Fee Payment tab
        setSelectedTab("payment-details");
        break;
      case 10:
        // Fee Payment -> Fee Payment tab
        setSelectedTab("payment-details");
        break;
      default:
        setSelectedTab("lead-management");
    }


    // Auto-scroll to content section
    if (contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };
  const renderTabContent = () => {
    return (
      <div className="flex-1 min-h-0 relative">
        {/* Lead Management Tab */}
        {visitedTabs.has("lead-management") && (
          <div id="section-lead-management" className={`animate-in fade-in duration-500 ${selectedTab !== "lead-management" ? "hidden" : ""}`}>
            <LeadManagementTab
              isMobile={isMobile}
              leadData={leadData}
              progressData={progressData}
              isReadOnly={isReadOnly}
              onAssignOwner={() => setShowOwnerModal(true)}
              onEditFollowUp={handleEditFollowUp}
              onUpdateStatus={handleUpdateFollowUpStatus}
              onAddFollowUp={handleAddFollowUp}
              onChangeStage={() => setShowStageModal(true)}
              onSetPriority={() => setShowPriorityModal(true)}
              onSetInteractionLevel={() => setShowInteractionLevelModal(true)}
              onOpenNotesModal={() => {
                setSelectedTab("notes");
                setInternalOpenNotesAddModal(true);
              }}
              handleStepClick={handleStepClick}
              setProgressData={setProgressData}
            />
          </div>
        )}

        {/* Application Details Tab */}
        {visitedTabs.has("application-details") && (
          <div id="section-application-details" className={selectedTab === "application-details" ? "" : "hidden"}>
            <ApplicationFormViewTab
              applicationId={progressData?.ApplicationId}
              leadUserId={leadId}
              leadData={leadData}
              progressData={progressData}
              isMobile={isMobile}
              refreshProgressJourney={refreshProgressJourney}
              onLeadDataRefresh={fetchLeadData}
              selectedTab={selectedTab}
            />
          </div>
        )}

        {/* Timeline Tab */}
        {visitedTabs.has("timeline") && (
          <div id="section-timeline" className={`h-full ${selectedTab !== "timeline" ? "hidden" : ""}`}>
            <TimelineView 
              leadId={leadId} 
              entityName={finalEntityName} 
              scrollTargetId="details-content-scroll-container" 
              isActive={selectedTab === "timeline"}
            />
          </div>
        )}

        {/* Payment Details Tab */}
        {visitedTabs.has("payment-details") && (
          <div id="section-payment-details" className={selectedTab === "payment-details" ? "" : "hidden"}>
            <PaymentDetailsTab
              isActive={selectedTab === "payment-details"}
              leadData={leadData}
              isReadOnly={isReadOnly}
              isInternational={leadData?.IsInternationalLead}
              isMobile={isMobile}
              progressData={progressData}
              refreshProgressJourney={refreshProgressJourney}
              refreshLeadData={fetchLeadData}
              leadId={leadId}
              onPaymentSuccess={(formId) => {
                // If there's an application form to open
                if (formId) {
                  setSelectedFormForApplication({ FormId: formId });
                  setIsComingFromPaymentFlow(true);
                  setShowQuickLeadModal(true);
                }
              }}
            />
          </div>
        )}

        {/* Follow-up Calendar Tab */}
        {visitedTabs.has("follow-up-calendar") && (
          <div id="section-follow-up-calendar" className={selectedTab === "follow-up-calendar" ? "" : "hidden"}>
            <CalendarTab
              isMobile={isMobile}
              leadData={leadData}
              initialDate={new URLSearchParams(window.location.search).get('date')}
              isStudent={leadData?.IsStudent}
              onOwnerChange={handleOwnerChange}
            />
          </div>
        )}

        {/* Notes Tab */}
        {visitedTabs.has("notes") && (
          <div id="section-notes" className={`h-full ${selectedTab !== "notes" ? "hidden" : ""}`}>
            <NotesTab
              isMobile={isMobile}
              leadData={leadData}
              isReadOnly={isReadOnly}
              openAddModalExternal={!isReadOnly && (openNotesAddModal || internalOpenNotesAddModal)}
              setOpenAddModalExternal={(val) => {
                if (isReadOnly) return;
                setInternalOpenNotesAddModal(val);
                if (setOpenNotesAddModal) setOpenNotesAddModal(val);
              }}
              onRefreshParent={() => onLeadUpdate && onLeadUpdate(leadId, 'update')}
              scrollTargetId="details-content-scroll-container"
            />
          </div>
        )}

        {/* Communications Tab */}
        {visitedTabs.has("communications") && (
          <div id="section-communications" className={selectedTab !== "communications" ? "hidden" : ""}>
            <CommunicationsTab leadId={leadId} isMobile={isMobile} initialChannelId={initialSubTab} />
          </div>
        )}

        {/* Offer Letter Tab */}
        {visitedTabs.has("offer-letter") && (
          <div id="section-offer-letter" className={selectedTab === "offer-letter" ? "" : "hidden"}>
            <OfferLetterTab leadId={leadId} leadData={leadData} isMobile={isMobile} onOfferLetterGenerated={fetchLeadData} />
          </div>
        )}
      </div>
    );
  };
  // Permission check (skip in modal mode - parent handles permissions)
  if (!isModal && !hasPermission("MLLV")) {
    return (
      <Error401 />
    )
  }

  // Don't render content until data is loaded (UI handled by global loader)
  if (loading || !leadData) {
    if (isModal) {
      return (
        <div className="flex h-64 items-center justify-center">
          <div className="text-center">
            <div className="border-primary-600 mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"></div>
            <p className="dark:text-dark-400 text-gray-500">Loading lead details...</p>
          </div>
        </div>
      );
    }
    return null;
  }

  // Show error state
  if (error && !leadData) {
    const errorContent = (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <h3 className="dark:text-dark-100 mb-2 text-lg font-medium text-gray-900">
            Error Loading Lead
          </h3>
          <p className="dark:text-dark-400 mb-4 text-gray-500">{error}</p>
          <Button
            onClick={() => isModal ? fetchLeadData() : window.location.reload()}
            variant="outlined"
            color="primary"
          >
            Retry
          </Button>
        </div>
      </div>
    );

    if (isModal) {
      return errorContent;
    }

    return (
      <Page
        title={`${finalEntityName} Details`}
        component="div"
        className="dark:bg-dark-800 min-h-screen bg-gray-50"
      >
        {errorContent}
      </Page>
    );
  }

  // Main content - used in both modal and page mode
  const mainContent = (
    <>
      {/* Error banner if API failed but fallback data is shown */}
      {error && leadData && (
        <div className="mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:bg-yellow-900/20">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                <strong>Warning:</strong> Using fallback data. API Error:{" "}
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex ${isModal ? "flex-1" : "h-screen"} overflow-hidden bg-white dark:bg-dark-900 shadow-2xl rounded-xl border border-gray-200 dark:border-dark-800`}
      >
        {/* Left Part: Lead Summary & Details (from Drawer) */}
        {!isMobile && (
          <div className="hidden md:flex flex-col w-[360px] lg:w-[400px] shrink-0 border-r border-gray-200 dark:border-dark-600 h-full overflow-hidden">
            <LeadRightDrawerContent
              user={primaryLeadData || leadData}
              hideTabs={true}
              onRefresh={() => fetchLeadData(true)}
              entityName={finalEntityName}
            />
          </div>
        )}

        {/* Right Part: Operations (Tabs & Content) */}
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden bg-[#f8fafc]/50 dark:bg-dark-900/40">
          {/* Related Leads Tabs */}
          {(() => {
            const rawUniqueRelatedLeads = Array.from(
              new Map(relatedLeads.map((item) => [item.Id || item.id, item])).values()
            );
            if (rawUniqueRelatedLeads.length <= 1) return null;

            const isLeadDuplicate = (leadItem) => {
              const lId = String(leadItem.Id || leadItem.id || '').toLowerCase();
              const activeId = String(activeLeadId || originalLeadId || '').toLowerCase();
              const detailsBasic = (activeId === lId && leadData?.BasicInformation) ? leadData.BasicInformation : null;

              const isDupVal =
                leadItem.IsDuplicate ??
                leadItem.isDuplicate ??
                detailsBasic?.IsDuplicate;

              const parentIdVal =
                leadItem.ParentLeadId ??
                leadItem.parentLeadId ??
                detailsBasic?.ParentLeadId;

              // If explicitly marked as duplicate or has a parent lead, it is a duplicate
              if (
                isDupVal === true ||
                isDupVal === 1 ||
                isDupVal === '1' ||
                isDupVal === 'true' ||
                Boolean(parentIdVal)
              ) {
                return true;
              }

              // If explicitly marked as NOT duplicate and has no parent lead, it's primary
              if (
                (isDupVal === false ||
                 isDupVal === 0 ||
                 isDupVal === '0' ||
                 isDupVal === 'false') &&
                !parentIdVal
              ) {
                return false;
              }

              // If it is the parent of any other lead in the list, it's primary
              const isParentOfAny = rawUniqueRelatedLeads.some((other) => {
                const pId = other.ParentLeadId || other.parentLeadId;
                return pId && String(pId).toLowerCase() === lId;
              });
              if (isParentOfAny) return false;

              // Fallback: if ParentLeadId is present and not matching this lead, it's duplicate
              if (parentIdVal) return true;

              return false;
            };

            const isLeadStudent = (leadItem) => {
              const lId = String(leadItem.Id || leadItem.id || '').toLowerCase();
              const activeId = String(activeLeadId || originalLeadId || '').toLowerCase();
              const detailsBasic = (activeId === lId && leadData) ? leadData : null;

              const isStuVal =
                leadItem.IsStudent ??
                leadItem.isStudent ??
                leadItem.is_student ??
                detailsBasic?.IsStudent ??
                detailsBasic?.isStudent;

              return Boolean(isStuVal === true || isStuVal === 1 || isStuVal === '1' || isStuVal === 'true');
            };

            // Primary lead always comes first, followed by duplicate leads
            const uniqueRelatedLeads = [...rawUniqueRelatedLeads].sort((a, b) => {
              const aDup = isLeadDuplicate(a);
              const bDup = isLeadDuplicate(b);
              if (!aDup && bDup) return -1;
              if (aDup && !bDup) return 1;
              const timeA = new Date(a.CreatedAt || a.createdAt || 0).getTime();
          const timeB = new Date(b.CreatedAt || b.createdAt || 0).getTime();
          if (timeB !== timeA) return timeB - timeA;
          const dispA = Number(a.DisplayLeadId || a.displayLeadId || 0);
          const dispB = Number(b.DisplayLeadId || b.displayLeadId || 0);
          return dispB - dispA;
            });

            return (
              <div className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700 px-4 pt-2 flex space-x-2 overflow-x-auto custom-scrollbar flex-shrink-0">
                {uniqueRelatedLeads.map((rLead) => {
                  const rLeadId = rLead.Id || rLead.id;
                  const currentActiveId = activeLeadId || originalLeadId;
                  const isActive = String(currentActiveId).toLowerCase() === String(rLeadId).toLowerCase();
                  const isDup = isLeadDuplicate(rLead);
                  const rawId = rLead.DisplayLeadId || rLead.displayLeadId || rLead.DisplayId || rLead.displayId || "";
                  const labelText = rawId 
                    ? (String(rawId).startsWith('#') ? String(rawId) : `#${rawId}`) 
                    : (rLead.FormName || rLead.formName || "Lead");
                  const handleClick = () => setActiveLeadId(rLeadId);
                  return (
                    <button
                      key={rLeadId}
                      onClick={handleClick}
                      className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap border-b-2 transition-colors cursor-pointer flex items-center gap-2 ${isActive ? "border-primary-600 text-primary-600 bg-primary-50 dark:bg-primary-900/20 font-bold" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"}`}
                    >
                      <span className="flex items-center gap-1.5">
                        {labelText}
                        {isDup ? (
                          <Badge color="warning" variant="filled" className="font-bold px-1.5 py-0 text-[9px] leading-tight">
                            D
                          </Badge>
                        ) : (
                          <Badge color="error" variant="filled" className="font-bold px-1.5 py-0 text-[9px] leading-tight">
                            P
                          </Badge>
                        )}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">
                        {rLead.CreatedAt || rLead.createdAt ? new Date(rLead.CreatedAt || rLead.createdAt).toLocaleDateString() : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
          {/* Top Navigation Bar */}
          <SidebarNavigation
            selectedTab={selectedTab}
            setSelectedTab={handleTabChange}
            isMobile={isMobile}
            showMobileSidebar={showMobileSidebar}
            setShowMobileSidebar={setShowMobileSidebar}
            selectedContentTab={selectedContentTab}
            setSelectedContentTab={setSelectedContentTab}
            entityName={finalEntityName}
            leadData={leadData}
            layout="horizontal"
          />

          <main className="flex-1 overflow-hidden pt-4 pb-0 bg-white dark:bg-dark-900 flex flex-col">
            <div id="details-content-scroll-container" className="flex-1 overflow-y-auto custom-scrollbar px-6 pb-12">
              {/* Show original header on mobile only since left panel is hidden */}
              {isMobile && (
                <div className="mb-6 shrink-0">
                  <Card className="p-4 mb-4">
                    <LeadBasicInfo
                      leadData={leadData}
                      isFavorite={isFavorite}
                      isFlaged={isFlaged}
                      selectedStage={localStage || leadData?.stage}
                      selectedSubStage={localSubStage || leadData?.subStage}
                      isMobile={isMobile}
                      onRefreshLead={handleRefreshLead}
                      onEditStage={() => setShowStageModal(true)}
                      onEditPriority={() => setShowPriorityModal(true)}
                      onEditInteractionLevel={() => setShowInteractionLevelModal(true)}
                      entityName={finalEntityName}
                    />


                  </Card>
                </div>
              )}

              {renderTabContent()}
            </div>
          </main>
        </div>
      </div>

      {/* Stage Change Confirmation Modal */}
      <ConfirmModal
        show={stageChangeConfirm.modalState.show}
        state={stageChangeConfirm.modalState.state}
        confirmLoading={stageChangeConfirm.modalState.loading}
        onClose={stageChangeConfirm.closeModal}
        onOk={stageChangeConfirm.handleConfirm}
        messages={stageChangeConfirm.messages}
        buttonColor="primary"
      />
      {/* Modals */}
      <StageChangeModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        leadData={leadData}
        onStageChange={handleStageChange}
      />

      {/* Action Modals */}
      <AssignOwnerModal
        isOpen={showOwnerModal}
        onClose={() => setShowOwnerModal(false)}
        leadData={leadData}
        currentOwnerName={leadData?.assignedOwner !== "N/A" ? leadData?.assignedOwner : null}
        onOwnerChange={handleOwnerChange}
      />

      <SetPriorityModal
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        leadData={leadData}
        handlePriorityChange={handlePriorityChange}
      />
      <SetInteractionLevelModal
        isOpen={showInteractionLevelModal}
        onClose={() => setShowInteractionLevelModal(false)}
        leadData={leadData}
        handleInteractionLevelChange={handleInteractionLevelChange}
      />

      <LeadAnalyticsModal
        isOpen={showAnalyticsModal}
        onClose={() => setShowAnalyticsModal(false)}
        leadData={leadData}
      />

      <WhatsAppMessageModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        leadData={leadData}
      />

      <EmailMessageModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        leadData={leadData}
      />

      <SMSMessageModal
        isOpen={showSMSModal}
        onClose={() => setShowSMSModal(false)}
        leadData={leadData}
      />

      <FollowUpScheduleModal
        key={editingFollowUp?.id || editingFollowUp?.FollowUpId || 'new-follow-up'}
        isOpen={showFollowUpModal}
        onClose={() => {
          setShowFollowUpModal(false);
          setEditingFollowUp(null);
        }}
        leadData={leadData}
        editingFollowUp={editingFollowUp}
        onScheduleFollowUp={handleFollowUpScheduled}
        onOwnerChange={handleOwnerChange}
      />

      {/* Status Update Modal - Same as FollowUpsCalendar */}
      <Transition appear show={showStatusModal} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative w-full max-w-2xl max-h-[90vh] transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all dark:bg-dark-800 flex flex-col border border-gray-200 dark:border-dark-600">
              {/* Modern Header */}
              <div className="px-6 py-5 border-b border-gray-200 dark:border-dark-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                      <PencilIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <DialogTitle as="h3" className="text-xl font-semibold text-gray-900 dark:text-white">
                        Update Follow-up Status
                      </DialogTitle>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Change the status and outcome of this follow-up
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowStatusModal(false)}
                    variant="flat"
                    isIcon
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
                {/* Current Follow-up Info */}
                {selectedFollowUpForStatus && (
                  <Card className="p-4 border border-gray-200 dark:border-dark-600">
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-dark-700 rounded-full">
                        <UserIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {leadData?.name || 'Lead'}
                        </h4>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                          {(selectedFollowUpForStatus?.assignedToName || selectedFollowUpForStatus?.AssignedTo || selectedFollowUpForStatus?.UserName) && (
                            <div className="flex items-center space-x-1">
                              <UserIcon className="w-4 h-4" />
                              <span>{selectedFollowUpForStatus?.assignedToName || selectedFollowUpForStatus?.AssignedTo || selectedFollowUpForStatus?.UserName}</span>
                            </div>
                          )}
                          {(selectedFollowUpForStatus?.scheduledTime || selectedFollowUpForStatus?.ScheduledTime) && (
                            <div className="flex items-center space-x-1">
                              <ClockIcon className="w-4 h-4" />
                              <span>{selectedFollowUpForStatus?.scheduledTime || selectedFollowUpForStatus?.ScheduledTime}</span>
                            </div>
                          )}
                          {selectedFollowUpForStatus?.StatusName && (
                            <Badge variant="soft" color="neutral">
                              {selectedFollowUpForStatus.StatusName}
                            </Badge>
                          )}
                        </div>

                        {/* Previous Remarks */}
                        {selectedFollowUpForStatus?.Remarks && (
                          <div className="mt-3 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-gray-500" />
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Previous Remarks</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                              {selectedFollowUpForStatus.Remarks}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Form Fields */}
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                  {/* Status Selection */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                      <div className="flex items-center space-x-2">
                        <CheckIcon className="w-4 h-4 text-primary-600" />
                        <span>Status</span>
                        <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <Select
                      value={statusFormData.statusId || ''}
                      onChange={(e) => {
                        const selectedStatus = statusOptions.find(s => s.id === parseInt(e.target.value));
                        setStatusFormData(prev => ({
                          ...prev,
                          status: selectedStatus?.name || '',
                          statusId: selectedStatus?.id || null
                        }));
                      }}
                      className="w-full"
                      placeholder="Choose status..."
                    >
                      <option value="">Choose status...</option>
                      {statusOptions.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                {/* Selected Event Info */}
                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                    <div className="flex items-center space-x-2">
                      <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-primary-600" />
                      <span>Remarks</span>
                      <span className="text-gray-400 text-xs">(Optional)</span>
                    </div>
                  </label>
                  <Textarea
                    value={statusFormData.remarks}
                    onChange={(e) => setStatusFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Add notes about this follow-up outcome..."
                    rows={4}
                    className="w-full resize-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-750">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Required fields marked with *</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => {
                        setShowStatusModal(false);
                        setStatusFormData({ status: '', statusId: null, outcome: '', outcomeId: null, eventTypeId: null, remarks: '' });
                      }}
                      variant="outlined"
                      color="neutral"
                      size="md"
                      className="min-w-[100px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleStatusUpdate}
                      variant="solid"
                      color="primary"
                      size="md"
                      disabled={!statusFormData.status}
                      className="min-w-[120px] shadow-lg"
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      Update Status
                    </Button>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      <CallModal
        isOpen={showCallModal}
        onClose={() => {
          setShowCallModal(false);
          // Note: The CallModal itself handles global widget state management
          // in its useEffect hooks, so no additional state management needed here
        }}
        leadData={leadData}
        onCallComplete={(callData) => {
          // You can add logic here to refresh call history or other components
          setShowCallModal(false);
        }}
      />

      <QuickLeadModal
        isOpen={showQuickLeadModal}
        onClose={() => {
          setShowQuickLeadModal(false);
          setSelectedFormForApplication(null);
          setIsComingFromPaymentFlow(false);
        }}
        leadId={leadId}
        categories={leadData?.categories}
        selectedForm={selectedFormForApplication}
        isApplicationForm={true}
        isComingFromPayment={isComingFromPaymentFlow}
        onLeadUpdated={() => {
          fetchLeadData(true);
          // If this is in a modal/drawer, close it fully after conversion to applicant
          if (onFullClose) onFullClose();
          else if (onClose) onClose();
        }}
      />
    </>
  );

  // In modal mode, return content directly without Page wrapper
  if (isModal) {
    return mainContent;
  }

  // In page mode, wrap with Page component
  return (
    <Page
      title="Lead Details"
      component="div"
      className={`dark:bg-dark-800 min-h-screen bg-gray-50 ${isMobile ? "mobile-container" : ""}`}
    >
      {mainContent}
    </Page>
  );
};

const LeadDetails = (props) => {
  const params = useParams();
  const leadId = props.leadIdProp || params.id;

  return (
    <LeadOptimisticStateProvider>
      <LeadDetailsInner {...props} />
    </LeadOptimisticStateProvider>
  );
};

export default LeadDetails;
